import * as THREE from 'three'
import { XRButton } from 'three/addons/webxr/XRButton.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import gsap from 'gsap';

// Configure GSAP for optimal performance with Three.js
gsap.ticker.lagSmoothing(0);
gsap.ticker.fps(60);

// Disable console logging in production
console.log = function(){};

let renderer;
let lPinchOn;
let rPinchOn;
let baseComposition;
let root;
let delta;
let bunModel;
let bun2Model;
let onionModel;
let tomatoModel;
let lettuceModel;
let cheeseModel;
let meatModel;

// Initialize the GLTF loader
// Shared material for meshes without unique textures/colors
const SHARED_STD = new THREE.MeshStandardMaterial({
    roughness: 0.5,
    metalness: 0.2,
    envMapIntensity: 1.0,
    side: THREE.FrontSide
});

// Function to enhance model materials without touching DoubleSide ones
function enhanceModelMaterials(model) {
    model.traverse((c) => {
        if (!c.isMesh) return;
        const m = c.material;

        // Preserve any materials explicitly set to DoubleSide
        if (m && m.side === THREE.DoubleSide) {
            // leave unchanged
        } else if (m && m.isMeshStandardMaterial) {
            m.roughness = 0.5;
            m.metalness = 0.2;
            m.envMapIntensity = 1.0;
            m.side = THREE.FrontSide;
        } else if (m && m.map) {
            const clone = SHARED_STD.clone();
            clone.map = m.map;
            c.material = clone;
        } else if (!m) {
            c.material = SHARED_STD;
        } else {
            // Non-standard material without texture: reuse shared params by assigning shared
            c.material = SHARED_STD;
        }

        c.castShadow = true;
        c.receiveShadow = true;
    });
}

const gltfLoader = new GLTFLoader();

// Load the bun1 model
gltfLoader.load(
    '/static/bun1.glb',
    (gltf) => {
        bunModel = gltf.scene;
        enhanceModelMaterials(bunModel);
        bunModel.scale.set(0.18 * 0.9 * 72, 0.18 * 0.36 * 400, 0.18 * 0.9 * 72); // Adjusted width to match depth
        console.log('Bun1 model loaded successfully');
        console.log('Bun1 initial scale:', bunModel.scale);
        // Log the bounding box size to check actual model dimensions
        const bbox = new THREE.Box3().setFromObject(bunModel);
        console.log('Bun1 bounding box size:', {
            width: bbox.max.x - bbox.min.x,
            height: bbox.max.y - bbox.min.y,
            depth: bbox.max.z - bbox.min.z
        });
    },
    (progress) => {
        console.log('Loading bun1 model:', (progress.loaded / progress.total * 100) + '%');
    },
    (error) => {
        console.error('Error loading bun1 model:', error);
    }
);

// Load the bun2 model
gltfLoader.load(
    '/static/bun2.glb',
    (gltf) => {
        bun2Model = gltf.scene;
        enhanceModelMaterials(bun2Model);
        bun2Model.scale.set(0.18 * 0.9 * 72, 0.18 * 0.36 * 400, 0.18 * 0.9 * 72); // Adjusted width to match depth
        console.log('Bun2 model loaded successfully');
        console.log('Bun2 initial scale:', bun2Model.scale);
        // Log the bounding box size to check actual model dimensions
        const bbox = new THREE.Box3().setFromObject(bun2Model);
        console.log('Bun2 bounding box size:', {
            width: bbox.max.x - bbox.min.x,
            height: bbox.max.y - bbox.min.y,
            depth: bbox.max.z - bbox.min.z
        });
    },
    (progress) => {
        console.log('Loading bun2 model:', (progress.loaded / progress.total * 100) + '%');
    },
    (error) => {
        console.error('Error loading bun2 model:', error);
    }
);

// Load the onion model
gltfLoader.load(
    '/static/onion.glb',
    (gltf) => {
        onionModel = gltf.scene;
        enhanceModelMaterials(onionModel);
        onionModel.scale.set(0.18 * 0.9 * 14 * 0.8, 0.18 * 0.36 * 22 * 0.8, 0.18 * 0.9 * 14 * 0.8); // Increased height to 22
        console.log('Onion model loaded successfully');
    },
    (progress) => {
        console.log('Loading onion model:', (progress.loaded / progress.total * 100) + '%');
    },
    (error) => {
        console.error('Error loading onion model:', error);
    }
);

// Load the meat model
gltfLoader.load(
    '/static/meat.glb',
    (gltf) => {
        meatModel = gltf.scene;
        enhanceModelMaterials(meatModel);
        meatModel.scale.set(0.18 * 0.9 * 11.2 * 0.8, 0.18 * 0.36 * 25 * 0.8, 0.18 * 0.9 * 11.2 * 0.8); // Fine-tuned dimensions
        // No rotation needed for meat model - keeping it right-side up
        console.log('Meat model loaded successfully');
    },
    (progress) => {
        console.log('Loading meat model:', (progress.loaded / progress.total * 100) + '%');
    },
    (error) => {
        console.error('Error loading meat model:', error);
    }
);

// Load the tomato model
gltfLoader.load(
    '/static/tomato.glb',
    (gltf) => {
        tomatoModel = gltf.scene;
        enhanceModelMaterials(tomatoModel);
        tomatoModel.scale.set(0.18 * 0.9 * 12 * 0.8, 0.18 * 0.36 * 24 * 0.8, 0.18 * 0.9 * 12 * 0.8); // Reduced to 80% of original size
        console.log('Tomato model loaded successfully');
    },
    (progress) => {
        console.log('Loading tomato model:', (progress.loaded / progress.total * 100) + '%');
    },
    (error) => {
        console.error('Error loading tomato model:', error);
    }
);

// Load the lettuce model
gltfLoader.load(
    '/static/lettuce.glb',
    (gltf) => {
        lettuceModel = gltf.scene;
        enhanceModelMaterials(lettuceModel);
        lettuceModel.scale.set(0.18 * 0.9 * 12 * 0.8, 0.18 * 0.36 * 32 * 0.8, 0.18 * 0.9 * 12 * 0.8); // Reduced to 80% of original size
        console.log('Lettuce model loaded successfully');
    },
    (progress) => {
        console.log('Loading lettuce model:', (progress.loaded / progress.total * 100) + '%');
    },
    (error) => {
        console.error('Error loading lettuce model:', error);
    }
);

// Load the cheese model
gltfLoader.load(
    '/static/cheese.glb',
    (gltf) => {
        cheeseModel = gltf.scene;
        enhanceModelMaterials(cheeseModel);
        cheeseModel.scale.set(0.18 * 0.9 * 12.6 * 0.8, 0.18 * 0.36 * 28 * 0.8, 0.18 * 0.9 * 12.6 * 0.8); // Reduced to 80% of current size
        // No rotation needed for cheese model - keeping it right-side up
        console.log('Cheese model loaded successfully');
    },
    (progress) => {
        console.log('Loading cheese model:', (progress.loaded / progress.total * 100) + '%');
    },
    (error) => {
        console.error('Error loading cheese model:', error);
    }
);

// Grab interaction variables
let leftHandGrabbing = false;
let rightHandGrabbing = false;
let grabbedObject = null;
let originalParent = null;
let originalPosition = null;
let originalRotation = null;
let grabDistance = 0.15; // Distance threshold for grabbing objects
let grabOffset = 0.05; // Offset distance to prevent hand-object intersection

// State tracking
let joined = true; // Track when the halves are joined (starting state)
let origPosA = null; // Original position of cube A within root
let origPosB = null; // Original position of cube B within root
let pinchDetectionEnabled = false; // Enable pinch detection after 2 seconds in WebXR

// Position tracking variables
const _aPos = new THREE.Vector3();
const _bPos = new THREE.Vector3();

// Scene
const scene = new THREE.Scene()
scene.background = new THREE.Color( 0x505050 );

// XR root container to hold all 3D content (hidden in 2D view)
const xrRoot = new THREE.Group();
xrRoot.visible = false; // hidden by default in non‑XR
scene.add(xrRoot);

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Camera
const camera = new THREE.PerspectiveCamera( 50, window.innerWidth / window.innerHeight, 0.1, 50 );
camera.position.set( 0, 1.6, 3 );
scene.add(camera)

// Renderer
renderer = new THREE.WebGLRenderer({ 
    antialias: false,
    shadowMap: {
        enabled: true,
        type: THREE.PCFSoftShadowMap // Use soft shadows for better quality
    }
});
renderer.setPixelRatio(window.devicePixelRatio);
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.xr.enabled = true;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
document.body.appendChild( renderer.domElement );

// Create XR button but hide it (we'll use the whole page instead)
const xrButton = XRButton.createButton( renderer, { 'optionalFeatures': [ 'hand-tracking' ] } );
xrButton.style.display = 'none'; // Hide the default button
document.body.appendChild( xrButton );

// Make the entire page clickable for entering WebXR
document.body.style.cursor = 'pointer';
document.body.addEventListener('click', () => {
    // Simply click the hidden XR button to trigger WebXR session
    xrButton.click();
});

// Add visual feedback for the clickable area
document.body.style.transition = 'background-color 0.3s ease';
document.body.addEventListener('mouseenter', () => {
    document.body.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
});
document.body.addEventListener('mouseleave', () => {
    document.body.style.backgroundColor = '';
});

// Base Composition (container for the cube)
const tempGeo = new THREE.BoxGeometry(0.3, 0.3, 0.3);
const tempMat = new THREE.MeshBasicMaterial({ 
    color: 0xffffff, 
    transparent: true,
    opacity: 0.0,
    depthTest: false,
    depthWrite: false
});
baseComposition = new THREE.Mesh(tempGeo, tempMat);
baseComposition.scale.set(1, 1, 1); // Scale the container cube to be thin
// Set initial position - will be repositioned when WebXR session starts
baseComposition.position.set(0, 1.6, 2); // Initial position at head height, 2m in front
scene.add(baseComposition);

// Auto-position composition at user's head height
let compositionPositioned = false;
let xrSessionStartTime = null;

function positionCompositionAtHeadHeight() {
    if (compositionPositioned || !xrSessionStartTime) return;
    
    // Wait 2 seconds after WebXR session starts
    const elapsedTime = Date.now() - xrSessionStartTime;
    if (elapsedTime < 1000) {
        return; // Wait for 1 seconds
    }
    
    // Get user's head position (camera position)
    const headHeight = camera.position.y;
    const headDistance = camera.position.z;
    
    console.log('After 2 seconds in WebXR - Camera position:', camera.position);
    console.log('Head height:', headHeight, 'Head distance:', headDistance);
    
    // Position composition at head height, slightly in front of user
    baseComposition.position.set(0, headHeight - 0.2, headDistance - 1.0);
    
    compositionPositioned = true;
    console.log('Composition positioned at:', baseComposition.position);
    console.log('Final height:', headHeight - 0.2, 'Final distance:', headDistance - 1.0);
}

// Listen for WebXR session start
renderer.xr.addEventListener('sessionstart', () => {
    console.log('WebXR session started - starting countdown');
    xrSessionStartTime = Date.now();
    compositionPositioned = false; // Reset so we can position again
    pinchDetectionEnabled = false; // Disable pinch detection initially
    // Clamp pixel ratio during XR for performance
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.2));
    // Add class to hide the instruction overlay
    document.body.classList.add('xr-presenting');
    // Show all 3D content only during XR session
    xrRoot.visible = true;
    
    // After 1.5 seconds, position the UI reference near the burger (giving time for composition to be ready)
    setTimeout(() => {
        // Wait for composition to be positioned first
        if (!compositionPositioned) {
            console.log('Composition not positioned yet, waiting...');
            return;
        }

        // Get the world position of the root (burger)
        const rootWorldPos = new THREE.Vector3();
        root.getWorldPosition(rootWorldPos);

        // Get camera position
        const cameraPos = new THREE.Vector3();
        camera.getWorldPosition(cameraPos);

        console.log('WebXR UI Positioning Debug:', {
            rootPosition: {
                x: rootWorldPos.x.toFixed(3),
                y: rootWorldPos.y.toFixed(3),
                z: rootWorldPos.z.toFixed(3)
            },
            cameraPosition: {
                x: cameraPos.x.toFixed(3),
                y: cameraPos.y.toFixed(3),
                z: cameraPos.z.toFixed(3)
            }
        });

        // Calculate distance from camera to burger
        const distanceToBurger = rootWorldPos.distanceTo(cameraPos);

        // Calculate angle between camera and burger in the XZ plane
        const angleToCamera = Math.atan2(
            rootWorldPos.x - cameraPos.x,
            rootWorldPos.z - cameraPos.z
        );

        // Use fixed 12 degree offset for WebXR session start
        const angleOffset = 12;
        const newAngle = angleToCamera - THREE.MathUtils.degToRad(angleOffset);

        // Calculate new position at same distance but rotated angle
        const newX = cameraPos.x + Math.sin(newAngle) * distanceToBurger;
        const newZ = cameraPos.z + Math.cos(newAngle) * distanceToBurger;

        console.log('WebXR UI Calculation Debug:', {
            distanceToBurger: distanceToBurger.toFixed(3),
            angleToCamera: THREE.MathUtils.radToDeg(angleToCamera).toFixed(1) + '°',
            angleOffset: angleOffset + '°',
            newPosition: {
                x: newX.toFixed(3),
                y: rootWorldPos.y.toFixed(3),
                z: newZ.toFixed(3)
            }
        });

        // Keep the same Y position as the burger
        openUIRef.position.set(newX, rootWorldPos.y, newZ);
        // Update mainUI position to match openUIRef
        updateMainUIPosition();
        
        // Show mainUI with scale animation for initial WebXR setup
        mainUI.scale.set(0, 0, 0); // Reset scale before animation
        showMainUIWithAnimation();
        
        console.log('UI reference positioned and panel made visible after positioning');
    }, 1500);
    
    // Enable pinch detection after 2 seconds
    setTimeout(() => {
        pinchDetectionEnabled = true;
        console.log('Pinch detection enabled after 2 seconds');
    }, 2000);
});

// Listen for WebXR session end
renderer.xr.addEventListener('sessionend', () => {
    console.log('WebXR session ended');
    xrSessionStartTime = null;
    compositionPositioned = false; // Reset for next session
    pinchDetectionEnabled = false; // Reset pinch detection for next session
    // Restore pixel ratio after XR
    renderer.setPixelRatio(window.devicePixelRatio);
    // Remove class to show the instruction overlay again
    document.body.classList.remove('xr-presenting');
    // Hide all 3D content when XR session ends
    xrRoot.visible = false;
});

// Create root group as parent container
root = new THREE.Group();

// Position the root relative to the base composition
root.position.set(0, 0, 0);

// Rotate the root (adjust these values as needed)
root.rotation.set(0, 0, 0); // X, Y, Z rotation in radians

// Add the root as a child of the base composition
baseComposition.add(root);

// Create a temporary cube A that will be replaced with the bun model
const cubeAGeometry = new THREE.BoxGeometry(1, 1, 1);
const cubeAMaterial = new THREE.MeshLambertMaterial({ 
    color: 0x00ff00, // Green color for temporary cube
    transparent: true,
    opacity: 0, // Make it invisible
    depthTest: true,
    depthWrite: true
});
const cubeA = new THREE.Mesh(cubeAGeometry, cubeAMaterial);

// Scale cube A to 0.8 of original size (0.18 * 0.9 * 0.8)
cubeA.scale.set(0.18 * 0.9 * 0.8, 0.18 * 0.36 * 0.8, 0.18 * 0.9 * 0.8);

// Position cube A up (initial gap will be set after switchStates initialization)
cubeA.position.set(0, 0.034, 0);

// Create topArrowPlane as a child of cubeA (texture will be loaded later)
let topArrowPlane;

// Replace cube A with bun model once it's loaded
if (bunModel) {
    bunModel.position.copy(cubeA.position);
    bunModel.scale.copy(cubeA.scale);
    cubeA.add(bunModel); // Add bun as child to maintain transformations
} else {
    // If model isn't loaded yet, set up a check to add it later
    const checkBunModel = setInterval(() => {
        if (bunModel) {
            bunModel.position.copy(cubeA.position);
            bunModel.scale.set(0.18 * 0.9 * 72, 0.18 * 0.36 * 400, 0.18 * 0.9 * 72); // Adjusted width to match depth
            cubeA.add(bunModel);
            clearInterval(checkBunModel);
        }
    }, 100);
}

// Create cube B (bottom cube) - invisible collision mesh
const cubeBGeometry = new THREE.BoxGeometry(1, 1, 1);
const cubeBMaterial = new THREE.MeshLambertMaterial({ 
    color: 0x4ecdc4, // Teal color for cube B
    transparent: true,
    opacity: 0, // Make it invisible
    depthTest: true,
    depthWrite: true
});
const cubeB = new THREE.Mesh(cubeBGeometry, cubeBMaterial);

// Scale cube B to 0.8 of original size (0.18 * 0.9 * 0.8)
cubeB.scale.set(0.18 * 0.9 * 0.8, 0.18 * 0.36 * 0.8, 0.18 * 0.9 * 0.8);

// Position cube B down (initial gap will be set after switchStates initialization)
cubeB.position.set(0, -0.034, 0);

// Replace cube B with bun2 model once it's loaded
if (bun2Model) {
    bun2Model.position.copy(cubeB.position);
    bun2Model.scale.copy(cubeB.scale);
    cubeB.add(bun2Model); // Add bun2 as child to maintain transformations
} else {
    // If model isn't loaded yet, set up a check to add it later
    const checkBun2Model = setInterval(() => {
        if (bun2Model) {
            bun2Model.position.copy(cubeB.position);
            bun2Model.scale.set(0.18 * 0.9 * 72, 0.18 * 0.36 * 400, 0.18 * 0.9 * 72); // Adjusted width to match depth
            cubeB.add(bun2Model);
            console.log('Bun2 added to cubeB:', {
                position: bun2Model.position,
                scale: bun2Model.scale,
                parentScale: cubeB.scale
            });
            clearInterval(checkBun2Model);
        }
    }, 100);
}

// Add both cubes as children of root
root.add(cubeA);
root.add(cubeB);

// Store original positions of the cubes within the root
origPosA = cubeA.position.clone();
origPosB = cubeB.position.clone();

console.log('Root container and child cubes created successfully!');
console.log('Root container:', root, 'Cube A:', cubeA, 'Cube B:', cubeB);
console.log('Original positions - Cube A:', origPosA, 'Cube B:', origPosB);

/**
 * Lights
 */
// Setup lighting for better material visualization
const ambientLight = new THREE.AmbientLight(0xffffff, 1.0) // Increased for brighter ambient lighting
scene.add(ambientLight)

// Main directional light (sun-like)
const mainLight = new THREE.DirectionalLight(0xffffff, 1.8); // Reduced intensity for balanced shadows
mainLight.position.set(4, 10, 5); // Moved light higher for softer shadows
mainLight.castShadow = true;
mainLight.shadow.mapSize.width = 1536;
mainLight.shadow.mapSize.height = 1536;
mainLight.shadow.camera.near = 0.1;
mainLight.shadow.camera.far = 100;
mainLight.shadow.camera.left = -5;
mainLight.shadow.camera.right = 5;
mainLight.shadow.camera.top = 5;
mainLight.shadow.camera.bottom = -5;
mainLight.shadow.bias = -0.0005; // Reduced bias for softer shadows
mainLight.shadow.normalBias = 0.04; // Increased for softer shadows on curves
mainLight.shadow.radius = 3; // Reduced blur for sharper shadows
scene.add(mainLight);

// Fill light for better detail visibility and shadows
const fillLight = new THREE.DirectionalLight(0x8899ff, 0.9); // Increased fill light for brighter shadows
fillLight.position.set(-4, 7, -4); // Moved higher for softer shadows
fillLight.castShadow = false;
scene.add(fillLight);

// Shadow frustum fitting for crisp, low-cost shadows
const _tmpBox = new THREE.Box3();
const _tmpVec = new THREE.Vector3();
const _bboxCorners = [
    new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3(),
    new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()
];

function fitShadowCameraToObject(light, object, margin = 0.15) {
    if (!light || !light.shadow || !light.shadow.camera || !object) return;
    const cam = light.shadow.camera;
    // Ensure world matrices are current
    object.updateWorldMatrix(true, true);
    light.updateMatrixWorld(true);
    cam.updateMatrixWorld(true);
    cam.updateProjectionMatrix();

    // Compute world-space bounding box of the object hierarchy
    _tmpBox.setFromObject(object);
    // If empty (e.g., not yet loaded), skip
    if (!_tmpBox || !isFinite(_tmpBox.min.x) || !isFinite(_tmpBox.max.x)) return;

    // Expand with margin
    _tmpBox.min.addScalar(-margin);
    _tmpBox.max.addScalar(margin);

    // Build 8 corners in world space
    const min = _tmpBox.min; const max = _tmpBox.max;
    _bboxCorners[0].set(min.x, min.y, min.z);
    _bboxCorners[1].set(max.x, min.y, min.z);
    _bboxCorners[2].set(min.x, max.y, min.z);
    _bboxCorners[3].set(max.x, max.y, min.z);
    _bboxCorners[4].set(min.x, min.y, max.z);
    _bboxCorners[5].set(max.x, min.y, max.z);
    _bboxCorners[6].set(min.x, max.y, max.z);
    _bboxCorners[7].set(max.x, max.y, max.z);

    // Transform corners into light camera space
    const invCamMatrix = cam.matrixWorldInverse.clone();
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;
    for (let i = 0; i < 8; i++) {
        _tmpVec.copy(_bboxCorners[i]).applyMatrix4(invCamMatrix);
        if (_tmpVec.x < minX) minX = _tmpVec.x; if (_tmpVec.x > maxX) maxX = _tmpVec.x;
        if (_tmpVec.y < minY) minY = _tmpVec.y; if (_tmpVec.y > maxY) maxY = _tmpVec.y;
        if (_tmpVec.z < minZ) minZ = _tmpVec.z; if (_tmpVec.z > maxZ) maxZ = _tmpVec.z;
    }

    // Set orthographic bounds in light space
    // Note: camera looks down -Z; near/far are positive distances along -Z
    cam.left = minX;
    cam.right = maxX;
    cam.bottom = minY;
    cam.top = maxY;
    cam.near = Math.max(0.1, -maxZ);
    cam.far = Math.max(cam.near + 0.1, -minZ);
    cam.updateProjectionMatrix();
    light.shadow.needsUpdate = true;
}

// Initial fit once scene objects exist
fitShadowCameraToObject(mainLight, root, 0.15);

// Create an array to track switch states (false = OFF, true = ON)
// [onion, tomato, lettuce, cheese, patty]
const switchStates = [false, true, true, false, true].map(state => Boolean(state)); // Ensure true/false values

/**
 * Joint References
 */
// part0: Invisible reference cube that will hold the onion model
const part0Geometry = new THREE.BoxGeometry(0.15, 0.01, 0.15);
const part0Material = new THREE.MeshBasicMaterial({ 
    color: 0xffff00,
    transparent: true,
    opacity: 0 // Make it invisible
});
const part0 = new THREE.Mesh(part0Geometry, part0Material);
part0.name = 'part0';
part0.visible = switchStates[0]; // Set initial visibility based on switch state
part0.scale.set(switchStates[0] ? 1 : 0, switchStates[0] ? 1 : 0, switchStates[0] ? 1 : 0);
scene.add(part0);

// Add onion model to part0 once it's loaded
if (onionModel) {
    onionModel.position.set(0, 0, 0);
    part0.add(onionModel);
} else {
    // If model isn't loaded yet, set up a check to add it later
    const checkOnionModel = setInterval(() => {
        if (onionModel) {
            onionModel.position.set(0, 0, 0);
            part0.add(onionModel);
            clearInterval(checkOnionModel);
        }
    }, 100);
}

// Create visible material for other parts
const visiblePartMaterial = new THREE.MeshBasicMaterial({ 
    color: 0xffff00,  // Yellow color
    transparent: false,
    opacity: 1.0
});

// part1: Invisible reference cube that will hold the tomato model
const part1 = new THREE.Mesh(part0Geometry, part0Material);
part1.name = 'part1';
part1.visible = switchStates[1]; // Set initial visibility based on switch state
part1.scale.set(switchStates[1] ? 1 : 0, switchStates[1] ? 1 : 0, switchStates[1] ? 1 : 0);
scene.add(part1);

// Add tomato model to part1 once it's loaded
if (tomatoModel) {
    tomatoModel.position.set(0, 0, 0);
    part1.add(tomatoModel);
} else {
    const checkTomatoModel = setInterval(() => {
        if (tomatoModel) {
            tomatoModel.position.set(0, 0, 0);
            part1.add(tomatoModel);
            clearInterval(checkTomatoModel);
        }
    }, 100);
}

// part2: Invisible reference cube that will hold the cheese model
const part2 = new THREE.Mesh(part0Geometry, part0Material);
part2.name = 'part2';
part2.visible = switchStates[2]; // Set initial visibility based on switch state
part2.scale.set(switchStates[2] ? 1 : 0, switchStates[2] ? 1 : 0, switchStates[2] ? 1 : 0);
scene.add(part2);

// Add lettuce model to part2 once it's loaded
if (lettuceModel) {
    lettuceModel.position.set(0, 0, 0);
    part2.add(lettuceModel);
} else {
    const checkLettuceModel = setInterval(() => {
        if (lettuceModel) {
            lettuceModel.position.set(0, 0, 0);
            part2.add(lettuceModel);
            clearInterval(checkLettuceModel);
        }
    }, 100);
}

// part3: Invisible reference cube that will hold the meat model
const part3 = new THREE.Mesh(part0Geometry, part0Material);
part3.name = 'part3';
part3.visible = switchStates[3]; // Set initial visibility based on switch state
part3.scale.set(switchStates[3] ? 1 : 0, switchStates[3] ? 1 : 0, switchStates[3] ? 1 : 0);
scene.add(part3);

// Add cheese model to part3 once it's loaded
if (cheeseModel) {
    cheeseModel.position.set(0, 0, 0);
    part3.add(cheeseModel);
} else {
    const checkCheeseModel = setInterval(() => {
        if (cheeseModel) {
            cheeseModel.position.set(0, 0, 0);
            part3.add(cheeseModel);
            clearInterval(checkCheeseModel);
        }
    }, 100);
}

// part4: Invisible reference cube (duplicate of part0 geometry and material)
const part4 = new THREE.Mesh(part0Geometry, part0Material);
part4.name = 'part4';
part4.visible = switchStates[4]; // Set initial visibility based on switch state
part4.scale.set(switchStates[4] ? 1 : 0, switchStates[4] ? 1 : 0, switchStates[4] ? 1 : 0);
scene.add(part4);

// Add meat model to part4 once it's loaded
if (meatModel) {
    meatModel.position.set(0, 0, 0);
    part4.add(meatModel);
} else {
    const checkMeatModel = setInterval(() => {
        if (meatModel) {
            meatModel.position.set(0, 0, 0);
            part4.add(meatModel);
            clearInterval(checkMeatModel);
        }
    }, 100);
}

const sphere = new THREE.SphereGeometry(0.002, 32, 32)
const defaultMat = new THREE.MeshBasicMaterial({ 
    color: 0xff0000, 
    opacity: 0,
    transparent: true 
})

const lThumbObj = new THREE.Mesh(sphere, defaultMat)
const lIndexObj = new THREE.Mesh(sphere, defaultMat)
const rThumbObj = new THREE.Mesh(sphere, defaultMat)
const rIndexObj = new THREE.Mesh(sphere, defaultMat)

scene.add(lThumbObj)
scene.add(lIndexObj)
scene.add(rThumbObj)
scene.add(rIndexObj)

/**
 * Pinch References
 */
const sphere2 = new THREE.SphereGeometry(0.006, 32, 32)
const defaultMat2 = new THREE.MeshLambertMaterial({ 
    color: 0xffffff,
    transparent: true,
    opacity: 0
})
const greenMat2 = new THREE.MeshLambertMaterial({ 
    color: 0x00ff00,
    transparent: true,
    opacity: 0
})

const lPinchSphere = new THREE.Mesh(sphere2, defaultMat2)
const rPinchSphere = new THREE.Mesh(sphere2, greenMat2)
scene.add(lPinchSphere)
scene.add(rPinchSphere)
lPinchSphere.visible = true; // Show pinch reference spheres
rPinchSphere.visible = true; // Show pinch reference spheres

// Create openUIRef sphere
const openUIRef = new THREE.Mesh(
    new THREE.SphereGeometry(0.01, 16, 16), // Small radius of 1cm
    new THREE.MeshBasicMaterial({ color: 0xffffff }) // White color
);
scene.add(openUIRef);
openUIRef.visible = false; // Hide the reference sphere
openUIRef.position.set(0, 1.6, -0.5); // Position it in front of the user at head height

// Create UI reference spheres for all parts
const part0UIRef = new THREE.Mesh(
    new THREE.SphereGeometry(0.01, 16, 16), // Small radius of 1cm
    new THREE.MeshBasicMaterial({ color: 0xffffff }) // White color
);
scene.add(part0UIRef);
part0UIRef.visible = false;
part0UIRef.position.copy(openUIRef.position);

const part1UIRef = new THREE.Mesh(
    new THREE.SphereGeometry(0.01, 16, 16),
    new THREE.MeshBasicMaterial({ color: 0xffffff })
);
scene.add(part1UIRef);
part1UIRef.visible = false;
part1UIRef.position.copy(openUIRef.position);

const part2UIRef = new THREE.Mesh(
    new THREE.SphereGeometry(0.01, 16, 16),
    new THREE.MeshBasicMaterial({ color: 0xffffff })
);
scene.add(part2UIRef);
part2UIRef.visible = false;
part2UIRef.position.copy(openUIRef.position);

const part3UIRef = new THREE.Mesh(
    new THREE.SphereGeometry(0.01, 16, 16),
    new THREE.MeshBasicMaterial({ color: 0xffffff })
);
scene.add(part3UIRef);
part3UIRef.visible = false;
part3UIRef.position.copy(openUIRef.position);

const part4UIRef = new THREE.Mesh(
    new THREE.SphereGeometry(0.01, 16, 16),
    new THREE.MeshBasicMaterial({ color: 0xffffff })
);
scene.add(part4UIRef);
part4UIRef.visible = false;
part4UIRef.position.copy(openUIRef.position);

/**
 * Main UI Plane
 */
// Create loaders
const textureLoader = new THREE.TextureLoader();
const fontLoader = new FontLoader();

// Load the UI textures
const mainUITexture = textureLoader.load('/static/ui_main.png');
mainUITexture.encoding = THREE.sRGBEncoding; // Ensure proper color encoding

// Load and create topArrowPlane
const topArrowTexture = textureLoader.load('/static/ui_arrow.png');
topArrowTexture.encoding = THREE.sRGBEncoding;
const topArrowPlaneGeometry = new THREE.PlaneGeometry(0.2016, 0.2016);
const topArrowPlaneMaterial = new THREE.MeshBasicMaterial({ 
    map: topArrowTexture,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 1.0,
    depthTest: false
});
topArrowPlane = new THREE.Mesh(topArrowPlaneGeometry, topArrowPlaneMaterial);
topArrowPlane.name = 'topArrowPlane';
topArrowPlane.position.set(0, 0.8, 0); // Shift up to be visible above the bun
// Counter-scale to keep the plane square despite parent's non-uniform scale
// Parent Y scale is 0.36 while X/Z are 0.9, so multiply Y by (0.9/0.36) = 2.5
topArrowPlane.scale.set(1, 2.5, 1);
topArrowPlane.renderOrder = 999; // Render on top
cubeA.add(topArrowPlane);

// Create bottomArrowPlane as a child of cubeB
let bottomArrowPlane;
const bottomArrowTexture = textureLoader.load('/static/ui_arrow.png');
bottomArrowTexture.encoding = THREE.sRGBEncoding;
const bottomArrowPlaneGeometry = new THREE.PlaneGeometry(0.2016, 0.2016);
const bottomArrowPlaneMaterial = new THREE.MeshBasicMaterial({ 
    map: bottomArrowTexture,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 1.0,
    depthTest: false
});
bottomArrowPlane = new THREE.Mesh(bottomArrowPlaneGeometry, bottomArrowPlaneMaterial);
bottomArrowPlane.name = 'bottomArrowPlane';
bottomArrowPlane.position.set(0, -0.8, 0); // Shift down to be visible below the bun
// Counter-scale to keep the plane square despite parent's non-uniform scale
bottomArrowPlane.scale.set(1, 2.5, 1);
bottomArrowPlane.renderOrder = 999; // Render on top
bottomArrowPlane.rotation.x = Math.PI; // Flip upside down
cubeB.add(bottomArrowPlane);

// Text state and cached fonts
let mainUIText;
let currentFont = null;
let mediumFont = null;  // Store medium font globally
let textMaterial = null;  // Store materials globally
let textMaterial3 = null;

// Dispose helper to prevent memory leaks
function safeRemove(node) {
    node?.traverse?.((o) => {
        if (o.geometry) o.geometry.dispose();
        if (o.material) {
            if (Array.isArray(o.material)) o.material.forEach((m) => m && m.dispose && m.dispose());
            else if (o.material.dispose) o.material.dispose();
        }
    });
    node?.parent?.remove(node);
}

let fontBlack, fontBold, fontMedium;
const fontPromises = [
  new Promise((r) => fontLoader.load('/static/fonts/helvetica-black.json', r)),
  new Promise((r) => fontLoader.load('/static/fonts/helvetica-bold.json', r)),
  new Promise((r) => fontLoader.load('/static/fonts/helvetiker_bold.typeface.json', r)),
];

Promise.all(fontPromises).then(([black, bold, medium]) => {
  fontBlack = black; fontBold = bold; fontMedium = medium;
  initMainUIText();
});

function initMainUIText() {
  currentFont = fontBlack;
  mediumFont = fontMedium;
  textMaterial = new THREE.MeshBasicMaterial({ color: 0xFFCF00 });
  textMaterial3 = new THREE.MeshBasicMaterial({ color: 0xFFFFFF });

  mainUIText = new THREE.Group();
  const fixedLeftPosition = -0.102;

  const initialBurgerName = getCurrentBurgerName();
  const initialKey = Object.entries(burgerNames).find(([_, value]) => value.name === initialBurgerName)?.[0] || '';
  const initialBurger = burgerNames[initialKey] || { description: 'Nothing but bun and \npure nothingness.', price: '10' };
  const initialPrice = initialBurger.price;
  const [descLine1, descLine2] = initialBurger.description.split('\n');

  // Description meshes
  const textGeometry3a = new TextGeometry(descLine1, { font: mediumFont, size: 0.011, height: 0, curveSegments: 12, bevelEnabled: false });
  const textGeometry3b = new TextGeometry(descLine2, { font: mediumFont, size: 0.011, height: 0, curveSegments: 12, bevelEnabled: false });
  textGeometry3a.computeBoundingBox();
  textGeometry3b.computeBoundingBox();
  const height3a = textGeometry3a.boundingBox.max.y - textGeometry3a.boundingBox.min.y;
  textGeometry3a.translate(0, -height3a * 2, 0.001);
  textGeometry3b.translate(0, -height3a * 3, 0.001);
  const textMesh3a = new THREE.Mesh(textGeometry3a, textMaterial3);
  const textMesh3b = new THREE.Mesh(textGeometry3b, textMaterial3);
  textMesh3a.position.set(0.001, -0.02 + 0.045, 0);
  textMesh3b.position.set(0.001, -0.025 + 0.045, 0);

  // Title meshes
  const textGeometry1 = new TextGeometry(initialBurgerName, { font: currentFont, size: 0.025, height: 0, curveSegments: 12, bevelEnabled: false });
  const textGeometry2 = new TextGeometry('BURGER', { font: currentFont, size: 0.025, height: 0, curveSegments: 12, bevelEnabled: false });
  textGeometry1.computeBoundingBox();
  textGeometry2.computeBoundingBox();
  const height1 = textGeometry1.boundingBox.max.y - textGeometry1.boundingBox.min.y;
  const height2 = textGeometry2.boundingBox.max.y - textGeometry2.boundingBox.min.y;
  textGeometry1.translate(0, height1 / 2, 0.001);
  textGeometry2.translate(0, -height2 / 2, 0.001);
  const textMesh1 = new THREE.Mesh(textGeometry1, textMaterial);
  const textMesh2 = new THREE.Mesh(textGeometry2, textMaterial);
  textMesh1.position.y = 0.004 + 0.04;
  textMesh2.position.y = -0.004 + 0.04;

  // Price mesh using cached bold
  const textGeometry4 = new TextGeometry(initialPrice, { font: fontBold, size: 0.028, height: 0, curveSegments: 12, bevelEnabled: false });
  const textMaterial4 = new THREE.MeshBasicMaterial({ color: 0xFFFFFF });
  const textMesh4 = new THREE.Mesh(textGeometry4, textMaterial4);
  textMesh4.position.set(0.12, -0.06 - 0.018, 0);

  mainUIText.add(textMesh1);
  mainUIText.add(textMesh2);
  mainUIText.add(textMesh3a);
  mainUIText.add(textMesh3b);
  mainUIText.add(textMesh4);
  mainUIText.position.x = fixedLeftPosition;

  mainUI.add(mainUIText);

  // Update handler using cached fonts and safe disposal
  window.updateBurgerText = function(newText) {
    const key = Object.entries(burgerNames).find(([_, value]) => value.name === newText)?.[0] || '';
    const description = burgerNames[key]?.description || 'Nothing but bun and \npure nothingness.';
    const price = burgerNames[key]?.price || '10';
    const [d1, d2] = description.split('\n');

    // Dispose and remove old meshes
    for (let i = mainUIText.children.length - 1; i >= 0; i--) {
      const child = mainUIText.children[i];
      safeRemove(child);
    }

    // Rebuild meshes
    const tg1 = new TextGeometry(newText, { font: currentFont, size: 0.025, height: 0, curveSegments: 12, bevelEnabled: false });
    const tg2 = new TextGeometry('BURGER', { font: currentFont, size: 0.025, height: 0, curveSegments: 12, bevelEnabled: false });
    const tg3a = new TextGeometry(d1, { font: mediumFont, size: 0.011, height: 0, curveSegments: 12, bevelEnabled: false });
    const tg3b = new TextGeometry(d2, { font: mediumFont, size: 0.011, height: 0, curveSegments: 12, bevelEnabled: false });
    tg1.computeBoundingBox();
    tg2.computeBoundingBox();
    tg3a.computeBoundingBox();
    tg3b.computeBoundingBox();
    const h1 = tg1.boundingBox.max.y - tg1.boundingBox.min.y;
    const h2 = tg2.boundingBox.max.y - tg2.boundingBox.min.y;
    const h3a = tg3a.boundingBox.max.y - tg3a.boundingBox.min.y;
    tg1.translate(0, h1 / 2, 0.001);
    tg2.translate(0, -h2 / 2, 0.001);
    tg3a.translate(0, -h3a * 2, 0.001);
    tg3b.translate(0, -h3a * 3, 0.001);

    const tm1 = new THREE.Mesh(tg1, textMaterial);
    const tm2 = new THREE.Mesh(tg2, textMaterial);
    const tm3a = new THREE.Mesh(tg3a, textMaterial3);
    const tm3b = new THREE.Mesh(tg3b, textMaterial3);
    tm1.position.y = 0.004 + 0.04;
    tm2.position.y = -0.004 + 0.04;
    tm3a.position.set(0.001, -0.02 + 0.045, 0);
    tm3b.position.set(0.001, -0.025 + 0.045, 0);

    const tg4 = new TextGeometry(price, { font: fontBold, size: 0.028, height: 0, curveSegments: 12, bevelEnabled: false });
    const tm4 = new THREE.Mesh(tg4, new THREE.MeshBasicMaterial({ color: 0xFFFFFF }));
    tm4.position.set(0.12, -0.06 - 0.018, 0);

    mainUIText.add(tm1);
    mainUIText.add(tm2);
    mainUIText.add(tm3a);
    mainUIText.add(tm3b);
    mainUIText.add(tm4);
    mainUIText.position.x = fixedLeftPosition;
  };
}

// Burger combination names mapping
const burgerNames = {
    '': { name: 'THE VOID', description: 'Nothing but bun and \npure nothingness.', price: '11' },
    'onion': { name: 'TEARJERKER', description: 'Sharp bite that \nbrings tears of joy.', price: '12' },
    'tomato': { name: 'RED HALO', description: 'Bright, juicy, \na halo of flavor.', price: '12' },
    'lettuce': { name: 'LEAF WHISPER', description: 'Crisp greens that \nspeak in silence.', price: '11' },
    'cheese': { name: 'MELT BOMB', description: 'Soft, molten, and \ndangerously good.', price: '13' },
    'patty': { name: 'SOLO TITAN', description: 'One patty. All power. \nNothing else.', price: '13' },
    'onion,tomato': { name: 'CRIMSON CRY', description: 'Bold and tangy, \nmakes your taste roar.', price: '13' },
    'onion,lettuce': { name: 'GREEN FANG', description: 'Fresh crunch with \na hidden snap.', price: '13' },
    'onion,cheese': { name: 'GOLDEN TEARS', description: 'Sweet onion wrapped \nin molten gold.', price: '13' },
    'onion,patty': { name: 'BEEFSTORM', description: 'Thunder of beef, \nflash of onion.', price: '15' },
    'tomato,lettuce': { name: 'GARDEN KISS', description: 'A soft kiss of \nfreshness and calm.', price: '15' },
    'tomato,cheese': { name: 'SUNBURST MELT', description: 'Sunny tomato draped \nin warm cheese.', price: '12' },
    'tomato,patty': { name: 'BLAZING BULL', description: 'Pure beef fury with \na tomato spark.', price: '13' },
    'lettuce,cheese': { name: 'EMERALD', description: 'Lush green bite \nwith golden warmth.', price: '15' },
    'lettuce,patty': { name: 'SAVANNAH', description: 'Wild and crisp from \nthe open fields.', price: '15' },
    'cheese,patty': { name: 'MIGHTY MELT', description: 'Heavy, cheesy, \nmelts the brave.', price: '17' },
    'onion,tomato,lettuce': { name: 'JUNGLE', description: 'Tangled greens \nand wild energy.', price: '17' },
    'onion,tomato,cheese': { name: 'INFERNO', description: 'Heat and fire tangled \nin golden bliss.', price: '17' },
    'onion,tomato,patty': { name: 'FIREFANG', description: 'Roaring beef with \na searing snap.', price: '17' },
    'onion,lettuce,cheese': { name: 'FROST FANG', description: 'Cool crunch biting \nthrough the melt.', price: '17' },
    'onion,lettuce,patty': { name: 'WILD HOWL', description: 'Rustic power with \na crisp growl.', price: '21' },
    'onion,cheese,patty': { name: 'MOLTEN BEAST', description: 'Lava cheese and \nroaring beef clash.', price: '17' },
    'tomato,lettuce,cheese': { name: 'SOLAR BLOOM', description: 'Bursting with sunny \ngarden charm.', price: '17' },
    'tomato,lettuce,patty': { name: 'SAVAGE', description: 'Fresh, fierce, wild \nand unstoppable.', price: '21' },
    'tomato,cheese,patty': { name: 'LAVA STACK', description: 'Beef and cheese \nerupting in glory.', price: '23' },
    'lettuce,cheese,patty': { name: 'THUNDER LEAF', description: 'Crunch strikes through \nmolten depths.', price: '21' },
    'onion,tomato,lettuce,cheese': { name: 'STORM CROWN', description: 'Crowned in greens, \nthunder below.', price: '21' },
    'onion,tomato,lettuce,patty': { name: 'JUNGLE KING', description: 'Wild reign of leaf \nand fire.', price: '23' },
    'onion,tomato,cheese,patty': { name: 'DRAGON FANG', description: 'Searing bite from \na molten beast.', price: '22' },
    'onion,lettuce,cheese,patty': { name: 'BEASTLORD', description: 'Crunch, melt, and \nprimal power.', price: '23' },
    'tomato,lettuce,cheese,patty': { name: 'PHOENIX BITE', description: 'Born from fire, \nfresh once more.', price: '25' },
    'onion,tomato,lettuce,cheese,patty': { name: 'OMEGA', description: 'Every layer. Every flavor. \nThe final boss.', price: '27' }
};

// Function to get current burger name based on ingredients
function getCurrentBurgerName() {
    // Ensure switchStates is valid
    if (!Array.isArray(switchStates) || switchStates.length !== 5) {
        console.error('Invalid switchStates:', switchStates);
        return 'Void';
    }

    // Fixed order of ingredients to match burgerNames mapping
    const ingredientNames = ['onion', 'tomato', 'lettuce', 'cheese', 'patty'];
    
    // Build ingredients list maintaining fixed order
    const ingredients = [];
    ingredientNames.forEach((name, index) => {
        if (switchStates[index] === true) {  // Strict check for true
            ingredients.push(name);
        }
    });
    
    // Generate key using the original order (no sorting)
    const key = ingredients.join(',');
    const burger = burgerNames[key] || { name: 'THE VOID', description: 'Nothing but bun and pure nothingness.' };
    
    // Detailed debug logging
    console.log('Burger Name Calculation:', {
        switchStates: switchStates.map((state, i) => `${ingredientNames[i]}: ${state}`),
        activeIngredients: ingredients,
        generatedKey: key,
        foundName: burger.name,
        description: burger.description,
        matchFound: key in burgerNames
    });
    
    return burger.name;
}

// Function to calculate gap based on active ingredients
function calculateGap() {
    const activeIngredients = switchStates.filter(state => state).length;
    const baseGap = 0.031; // Maximum gap with all ingredients
    const gapPerIngredient = baseGap / 5; // Divide base gap by total possible ingredients
    return gapPerIngredient * (activeIngredients + 1); // +1 to maintain minimum gap
}

// Create an array to track switch interaction cooldowns
const switchCooldowns = [0, 0, 0, 0, 0];
const SWITCH_COOLDOWN_TIME = 500; // 500ms cooldown between toggles

// Load both ON and OFF textures for each switch
const switchTextures = [
    {
        OFF: textureLoader.load('/static/ui_switch0_OFF.png'),
        ON: textureLoader.load('/static/ui_switch0_ON.png')
    },
    {
        OFF: textureLoader.load('/static/ui_switch1_OFF.png'),
        ON: textureLoader.load('/static/ui_switch1_ON.png')
    },
    {
        OFF: textureLoader.load('/static/ui_switch2_OFF.png'),
        ON: textureLoader.load('/static/ui_switch2_ON.png')
    },
    {
        OFF: textureLoader.load('/static/ui_switch3_OFF.png'),
        ON: textureLoader.load('/static/ui_switch3_ON.png')
    },
    {
        OFF: textureLoader.load('/static/ui_switch4_OFF.png'),
        ON: textureLoader.load('/static/ui_switch4_ON.png')
    }
];

// Set encoding for all textures
switchTextures.forEach(texturePair => {
    texturePair.OFF.encoding = THREE.sRGBEncoding;
    texturePair.ON.encoding = THREE.sRGBEncoding;
});

// Initialize with correct textures based on initial states
const part0UITexture = switchTextures[0].OFF; // onion OFF
const part1UITexture = switchTextures[1].ON;  // tomato ON
const part2UITexture = switchTextures[2].ON;  // lettuce ON
const part3UITexture = switchTextures[3].OFF; // cheese OFF
const part4UITexture = switchTextures[4].ON;  // patty ON

// Create a plane for the UI
const mainUIGeometry = new THREE.PlaneGeometry(0.22, 0.22); // 0.22 x 0.22 meters square
const mainUIMaterial = new THREE.MeshBasicMaterial({
    map: mainUITexture,
    transparent: true,
    opacity: 1.0,
    side: THREE.FrontSide // Use FrontSide for UI planes
});
const mainUI = new THREE.Mesh(mainUIGeometry, mainUIMaterial);
mainUI.position.set(0, 1.6, -1); // Position it at head height, 1 meter in front
mainUI.visible = false; // Start invisible until WebXR positioning is complete
mainUI.scale.set(0, 0, 0); // Start with scale 0
scene.add(mainUI);

// Create UI planes for all parts with 180x70 proportions
const partUIGeometry = new THREE.PlaneGeometry(0.140, 0.0547); // 14.0cm x 5.47cm

const part0UIMaterial = new THREE.MeshBasicMaterial({
    map: part0UITexture,
    transparent: true,
    opacity: 1.0,
    side: THREE.FrontSide
});
const part0UI = new THREE.Mesh(partUIGeometry, part0UIMaterial);
part0UI.position.copy(mainUI.position);
part0UI.visible = false;
scene.add(part0UI);

const part1UIMaterial = new THREE.MeshBasicMaterial({
    map: part1UITexture,
    transparent: true,
    opacity: 1.0,
    side: THREE.FrontSide
});
const part1UI = new THREE.Mesh(partUIGeometry, part1UIMaterial);
part1UI.position.copy(mainUI.position);
part1UI.visible = false;
scene.add(part1UI);

const part2UIMaterial = new THREE.MeshBasicMaterial({
    map: part2UITexture,
    transparent: true,
    opacity: 1.0,
    side: THREE.FrontSide
});
const part2UI = new THREE.Mesh(partUIGeometry, part2UIMaterial);
part2UI.position.copy(mainUI.position);
part2UI.visible = false;
scene.add(part2UI);

const part3UIMaterial = new THREE.MeshBasicMaterial({
    map: part3UITexture,
    transparent: true,
    opacity: 1.0,
    side: THREE.FrontSide
});
const part3UI = new THREE.Mesh(partUIGeometry, part3UIMaterial);
part3UI.position.copy(mainUI.position);
part3UI.visible = false;
scene.add(part3UI);

const part4UIMaterial = new THREE.MeshBasicMaterial({
    map: part4UITexture,
    transparent: true,
    opacity: 1.0,
    side: THREE.FrontSide
});
const part4UI = new THREE.Mesh(partUIGeometry, part4UIMaterial);
part4UI.position.copy(mainUI.position);
part4UI.visible = false;
scene.add(part4UI);

/**
 * Hands
 */
var hand1, hand2;

hand1 = renderer.xr.getHand( 0 );
scene.add( hand1 );

hand2 = renderer.xr.getHand( 1 );
scene.add( hand2 );

const handsGroup = new THREE.Group();
handsGroup.add(hand1, hand2);
scene.add(handsGroup);
handsGroup.visible = false;

// Reparent all 3D content under xrRoot so it can be shown/hidden per XR session
const _objectsToGroup = [
    baseComposition,
    // ingredient holders
    part0, part1, part2, part3, part4,
    // fingertip helpers
    lThumbObj, lIndexObj, rThumbObj, rIndexObj,
    // pinch refs
    lPinchSphere, rPinchSphere,
    // UI refs and planes
    openUIRef, part0UIRef, part1UIRef, part2UIRef, part3UIRef, part4UIRef,
    mainUI, part0UI, part1UI, part2UI, part3UI, part4UI,
    // lights
    ambientLight, mainLight, fillLight,
    // hands
    handsGroup
];
_objectsToGroup.forEach(obj => { if (obj && obj.parent !== xrRoot) xrRoot.add(obj); });

const clock = new THREE.Clock();

// Function to animate topArrowPlane and bottomArrowPlane up and down
function animateTopArrowPlane() {
    if (topArrowPlane) {
        const time = clock.getElapsedTime();
        const baseY = 1; // Base Y position
        const amplitude = 0.05; // How much it moves up and down
        const frequency = 8; // Speed of oscillation
        topArrowPlane.position.y = baseY + Math.sin(time * frequency) * amplitude;
    }
    if (bottomArrowPlane) {
        const time = clock.getElapsedTime();
        const baseY = -0.8; // Base Y position
        const amplitude = 0.05; // How much it moves up and down
        const frequency = 8; // Speed of oscillation
        bottomArrowPlane.position.y = baseY - Math.sin(time * frequency) * amplitude; // Opposite direction (negative)
    }
}

renderer.setAnimationLoop( function () {
    delta = clock.getDelta();
    gsap.ticker.tick(delta);

    renderer.render( scene, camera );
    
 
    handTracking();
    openingTracking();
    freeMoveTracking();
    rejoinTracking();
    openStateTracking();
    contentPartsTracking();
    positionCompositionAtHeadHeight();
    animateTopArrowPlane(); 

} );

// Function to check if a point intersects with a plane
function checkPointPlaneIntersection(point, plane) {
    if (!plane.visible) return false;

    // Get plane's world position and dimensions
    const planePos = new THREE.Vector3();
    plane.getWorldPosition(planePos);
    
    // Get plane's world quaternion
    const planeQuat = new THREE.Quaternion();
    plane.getWorldQuaternion(planeQuat);
    
    // Create a local coordinate system for the plane
    const planeMatrix = new THREE.Matrix4();
    planeMatrix.makeRotationFromQuaternion(planeQuat);
    
    // Transform point to plane's local space
    const localPoint = point.clone().sub(planePos);
    localPoint.applyMatrix4(planeMatrix.invert());
    
    // Get plane's dimensions
    const width = plane.geometry.parameters.width / 2;
    const height = plane.geometry.parameters.height / 2;
    
    // Check if point is within plane bounds
    return Math.abs(localPoint.x) < width && 
           Math.abs(localPoint.y) < height && 
           Math.abs(localPoint.z) < 0.02; // Small threshold for depth
}

// Function to toggle switch state
function toggleSwitch(switchIndex) {
    const currentTime = Date.now();
    
    // Check cooldown
    if (currentTime - switchCooldowns[switchIndex] < SWITCH_COOLDOWN_TIME) {
        return;
    }
    
    // Update cooldown
    switchCooldowns[switchIndex] = currentTime;
    
    // Toggle state with strict boolean
    switchStates[switchIndex] = !switchStates[switchIndex];
    // Ensure it's a boolean
    switchStates[switchIndex] = Boolean(switchStates[switchIndex]);
    
    // Update texture
    const switchUI = [part0UI, part1UI, part2UI, part3UI, part4UI][switchIndex];
    switchUI.material.map = switchTextures[switchIndex][switchStates[switchIndex] ? 'ON' : 'OFF'];
    switchUI.material.needsUpdate = true;

    // Update burger part with scale animation
    const burgerPart = [part0, part1, part2, part3, part4][switchIndex];
    
    if (switchStates[switchIndex]) {
        // Turning ON - Start with scale 0 and animate to 1
        burgerPart.visible = true;
        burgerPart.scale.set(0, 0, 0);
        gsap.to(burgerPart.scale, {
            x: 1,
            y: 1,
            z: 1,
            duration: 0.3,
            ease: "back.out(1.7)"
        });
    } else {
        // Turning OFF - Animate from 1 to 0 and hide
        gsap.to(burgerPart.scale, {
            x: 0,
            y: 0,
            z: 0,
            duration: 0.2,
            ease: "power2.in",
            onComplete: () => {
                burgerPart.visible = false;
            }
        });
    }

    // Always update the text when switches change
    const newBurgerName = getCurrentBurgerName();
    console.log('Toggle Switch Debug:', {
        switchIndex: switchIndex,
        newState: switchStates[switchIndex],
        allSwitchStates: [...switchStates],
        resultingName: newBurgerName
    });
    
    if (window.updateBurgerText) {
        window.updateBurgerText(newBurgerName);
    }

    // If burger is joined, update the gap based on active ingredients
    if (joined) {
        try {
            const currentGap = calculateGap();
            const activeCount = switchStates.filter(state => state).length;
            
            cubeA.position.set(0, currentGap, 0);
            cubeB.position.set(0, -currentGap, 0);
            
            // Update original positions to match new gap
            origPosA = cubeA.position.clone();
            origPosB = cubeB.position.clone();
            
            console.log('Switch toggle - Gap adjustment complete:', {
                gap: currentGap,
                activeIngredients: activeCount,
                cubeAPos: cubeA.position.clone(),
                cubeBPos: cubeB.position.clone()
            });
        } catch (error) {
            console.error('Error during switch toggle gap adjustment:', error);
        }
    }

    // Refit the shadow frustum to current burger bounds
    fitShadowCameraToObject(mainLight, root, 0.15);
}

function handTracking() {
    // Debug: log the handedness to see what's happening
    console.log('Hand1 handedness:', hand1?.handedness, 'Hand2 handedness:', hand2?.handedness);
    
    // Force hand1 to always be left hand tracking, hand2 to be right
    // This gives you consistent control regardless of WebXR's internal assignment
    const leftHand = hand1;
    const rightHand = hand2;
    
    // Left Hand Joint References (using hand1 consistently)
    if (leftHand && leftHand.joints && leftHand.joints['thumb-tip']) {
        lThumbObj.position.x = leftHand.joints['thumb-tip'].position.x;
        lThumbObj.position.y = leftHand.joints['thumb-tip'].position.y;
        lThumbObj.position.z = leftHand.joints['thumb-tip'].position.z;
        lThumbObj.rotation.x = leftHand.joints['thumb-tip'].rotation.x;
        lThumbObj.rotation.y = leftHand.joints['thumb-tip'].rotation.y;
        lThumbObj.rotation.z = leftHand.joints['thumb-tip'].rotation.z;
    }

    if (leftHand && leftHand.joints && leftHand.joints['index-finger-tip']) {
        lIndexObj.position.x = leftHand.joints['index-finger-tip'].position.x;
        lIndexObj.position.y = leftHand.joints['index-finger-tip'].position.y;
        lIndexObj.position.z = leftHand.joints['index-finger-tip'].position.z;
        lIndexObj.rotation.x = leftHand.joints['index-finger-tip'].rotation.x;
        lIndexObj.rotation.y = leftHand.joints['index-finger-tip'].rotation.y;
        lIndexObj.rotation.z = leftHand.joints['index-finger-tip'].rotation.z;

        // Check for UI interactions with left index finger
        const uiPlanes = [part0UI, part1UI, part2UI, part3UI, part4UI];
        uiPlanes.forEach((plane, index) => {
            if (checkPointPlaneIntersection(lIndexObj.position, plane)) {
                toggleSwitch(index);
            }
        });
    }

    // Right Hand Joint References (using hand2 consistently)
    if (rightHand && rightHand.joints && rightHand.joints['thumb-tip']) {
        rThumbObj.position.x = rightHand.joints['thumb-tip'].position.x;
        rThumbObj.position.y = rightHand.joints['thumb-tip'].position.y;
        rThumbObj.position.z = rightHand.joints['thumb-tip'].position.z;
        rThumbObj.rotation.x = rightHand.joints['thumb-tip'].rotation.x;
        rThumbObj.rotation.y = rightHand.joints['thumb-tip'].rotation.y;
        rThumbObj.rotation.z = rightHand.joints['thumb-tip'].rotation.z;
    }

    if (rightHand && rightHand.joints && rightHand.joints['index-finger-tip']) {
        rIndexObj.position.x = rightHand.joints['index-finger-tip'].position.x;
        rIndexObj.position.y = rightHand.joints['index-finger-tip'].position.y;
        rIndexObj.position.z = rightHand.joints['index-finger-tip'].position.z;
        rIndexObj.rotation.x = rightHand.joints['index-finger-tip'].rotation.x;
        rIndexObj.rotation.y = rightHand.joints['index-finger-tip'].rotation.y;
        rIndexObj.rotation.z = rightHand.joints['index-finger-tip'].rotation.z;

        // Check for UI interactions with right index finger
        const uiPlanes = [part0UI, part1UI, part2UI, part3UI, part4UI];
        uiPlanes.forEach((plane, index) => {
            if (checkPointPlaneIntersection(rIndexObj.position, plane)) {
                toggleSwitch(index);
            }
        });
    }

    // Left Hand Pinch Detection (gated by pinchDetectionEnabled)
    const lIndexThumbDist = lIndexObj.position.distanceTo(lThumbObj.position);

    if (pinchDetectionEnabled && lIndexThumbDist < 0.02) {
        lPinchSphere.position.x = lThumbObj.position.x;
        lPinchSphere.position.y = lThumbObj.position.y;
        lPinchSphere.position.z = lThumbObj.position.z;
        
        // Copy thumb rotation to pinch reference
        lPinchSphere.rotation.x = lThumbObj.rotation.x;
        lPinchSphere.rotation.y = lThumbObj.rotation.y;
        lPinchSphere.rotation.z = lThumbObj.rotation.z;
        
        // Left hand pinch detected
        lPinchOn = true;
        
        // Check if we can grab the root object (only in joined mode)
        if (!leftHandGrabbing && !rightHandGrabbing && joined) {
            // Get the world position of the root for accurate distance calculation
            const rootWorldPosition = new THREE.Vector3();
            root.getWorldPosition(rootWorldPosition);
            const distanceToRoot = lPinchSphere.position.distanceTo(rootWorldPosition);
            if (distanceToRoot < grabDistance) {
                // Start grabbing the root
                leftHandGrabbing = true;
                grabbedObject = root;
                originalParent = root.parent;
                originalPosition = root.position.clone();
                originalRotation = root.rotation.clone();
                
                // Store the world rotation before parenting
                const worldQuaternion = root.getWorldQuaternion(new THREE.Quaternion());
                
                // Parent the root to the pinch reference
                lPinchSphere.add(root);
                
                // Position the object with an offset to prevent hand intersection
                root.position.set(0, 0, -grabOffset); // Offset backward from pinch reference
                
                // Calculate and set the local rotation to maintain the original world rotation
                const parentWorldQuaternion = lPinchSphere.getWorldQuaternion(new THREE.Quaternion());
                const localQuaternion = worldQuaternion.clone().multiply(parentWorldQuaternion.invert());
                root.quaternion.copy(localQuaternion);
                
                console.log('Left hand grabbed the root object');
            }
        }

    } else {
        lPinchOn = false;
        
        // Release grabbed object if left hand stops pinching
        if (leftHandGrabbing && grabbedObject) {
            leftHandGrabbing = false;
            
            // Get the current world position and rotation of the root before reparenting
            const currentWorldPosition = new THREE.Vector3();
            const currentWorldQuaternion = new THREE.Quaternion();
            grabbedObject.getWorldPosition(currentWorldPosition);
            grabbedObject.getWorldQuaternion(currentWorldQuaternion);
            
            // Restore the object to its original parent
            originalParent.add(grabbedObject);
            
            // Convert world position to local position within baseComposition
            const localPosition = new THREE.Vector3();
            localPosition.copy(currentWorldPosition);
            originalParent.worldToLocal(localPosition);
            
            // Set the root to stay at its current position (no bounce back)
            grabbedObject.position.copy(localPosition);
            
            // Convert world rotation to local rotation within the original parent
            const parentWorldQuaternion = originalParent.getWorldQuaternion(new THREE.Quaternion());
            const localQuaternion = currentWorldQuaternion.clone().multiply(parentWorldQuaternion.invert());
            grabbedObject.quaternion.copy(localQuaternion);
            
            // Reset grab state
            grabbedObject = null;
            originalParent = null;
            originalPosition = null;
            originalRotation = null;
            
            // Update openUIRef position when releasing a closed burger
            if (joined) {
                // Get the world position of the root (burger)
                const rootWorldPos = new THREE.Vector3();
                root.getWorldPosition(rootWorldPos);

                // Get camera position
                const cameraPos = new THREE.Vector3();
                camera.getWorldPosition(cameraPos);

                // Calculate distance from camera to burger
                const distanceToBurger = rootWorldPos.distanceTo(cameraPos);

                // Calculate angle between camera and burger in the XZ plane
                const angleToCamera = Math.atan2(
                    rootWorldPos.x - cameraPos.x,
                    rootWorldPos.z - cameraPos.z
                );

                // Calculate angle based on distance
                const angleOffset = distanceToBurger <= 0.6 ? 30 : 20; // 0.6 meters = 60 cm
                const newAngle = angleToCamera - THREE.MathUtils.degToRad(angleOffset);

                // Calculate new position at same distance but rotated angle
                const newX = cameraPos.x + Math.sin(newAngle) * distanceToBurger;
                const newZ = cameraPos.z + Math.cos(newAngle) * distanceToBurger;

                // Keep the same Y position as the burger
                openUIRef.position.set(newX, rootWorldPos.y, newZ);
                // Update mainUI position to match openUIRef
                updateMainUIPosition();
                // Show mainUI with scale animation
                mainUI.scale.set(0, 0, 0); // Reset scale before animation
                showMainUIWithAnimation();
            }
            
            console.log('Left hand released the root object - staying in current position and rotation');

            // After moving root, refit shadow frustum
            fitShadowCameraToObject(mainLight, root, 0.15);
        }
    }

    // Right Hand Pinch Detection (gated by pinchDetectionEnabled)
    const rIndexThumbDist = rIndexObj.position.distanceTo(rThumbObj.position);
    
    // Debug: log pinch distance
    if (pinchDetectionEnabled && rIndexThumbDist < 0.05) { // Log when getting close to pinch
        console.log('Right hand pinch distance:', rIndexThumbDist, 'Threshold: 0.02');
    }
    
    // Always update the pinch reference sphere position when pinching
    if (pinchDetectionEnabled && rIndexThumbDist < 0.02) {
        rPinchSphere.position.x = rThumbObj.position.x;
        rPinchSphere.position.y = rThumbObj.position.y;
        rPinchSphere.position.z = rThumbObj.position.z;
        
        // Copy thumb rotation to pinch reference
        rPinchSphere.rotation.x = rThumbObj.rotation.x;
        rPinchSphere.rotation.y = rThumbObj.rotation.y;
        rPinchSphere.rotation.z = rThumbObj.rotation.z;
        
        rPinchOn = true;
        
        // Check if we can grab the root object (only in joined mode)
        if (!leftHandGrabbing && !rightHandGrabbing && joined) {
            // Get the world position of the root for accurate distance calculation
            const rootWorldPosition = new THREE.Vector3();
            root.getWorldPosition(rootWorldPosition);
            const distanceToRoot = rPinchSphere.position.distanceTo(rootWorldPosition);
            if (distanceToRoot < grabDistance) {
                // Start grabbing the root
                rightHandGrabbing = true;
                grabbedObject = root;
                originalParent = root.parent;
                originalPosition = root.position.clone();
                originalRotation = root.rotation.clone();
                
                // Store the world rotation before parenting
                const worldQuaternion = root.getWorldQuaternion(new THREE.Quaternion());
                
                // Parent the root to the pinch reference
                rPinchSphere.add(root);
                
                // Position the object with an offset to prevent hand intersection
                root.position.set(0, 0, -grabOffset); // Offset backward from pinch reference
                
                // Calculate and set the local rotation to maintain the original world rotation
                const parentWorldQuaternion = rPinchSphere.getWorldQuaternion(new THREE.Quaternion());
                const localQuaternion = worldQuaternion.clone().multiply(parentWorldQuaternion.invert());
                root.quaternion.copy(localQuaternion);
                
                console.log('Right hand grabbed the root object');
            }
        }

    } else {
        rPinchOn = false;
        
        // Release grabbed object if right hand stops pinching
        if (rightHandGrabbing && grabbedObject) {
            rightHandGrabbing = false;
            
            // Get the current world position and rotation of the root before reparenting
            const currentWorldPosition = new THREE.Vector3();
            const currentWorldQuaternion = new THREE.Quaternion();
            grabbedObject.getWorldPosition(currentWorldPosition);
            grabbedObject.getWorldQuaternion(currentWorldQuaternion);
            
            // Restore the object to its original parent
            originalParent.add(grabbedObject);
            
            // Convert world position to local position within baseComposition
            const localPosition = new THREE.Vector3();
            localPosition.copy(currentWorldPosition);
            originalParent.worldToLocal(localPosition);
            
            // Set the root to stay at its current position (no bounce back)
            grabbedObject.position.copy(localPosition);
            
            // Convert world rotation to local rotation within the original parent
            const parentWorldQuaternion = originalParent.getWorldQuaternion(new THREE.Quaternion());
            const localQuaternion = currentWorldQuaternion.clone().multiply(parentWorldQuaternion.invert());
            grabbedObject.quaternion.copy(localQuaternion);
            
            // Reset grab state
            grabbedObject = null;
            originalParent = null;
            originalPosition = null;
            originalRotation = null;
            
            // Update openUIRef position when releasing a closed burger
            if (joined) {
                // Get the world position of the root (burger)
                const rootWorldPos = new THREE.Vector3();
                root.getWorldPosition(rootWorldPos);

                // Get camera position
                const cameraPos = new THREE.Vector3();
                camera.getWorldPosition(cameraPos);

                // Calculate distance from camera to burger
                const distanceToBurger = rootWorldPos.distanceTo(cameraPos);

                // Calculate angle between camera and burger in the XZ plane
                const angleToCamera = Math.atan2(
                    rootWorldPos.x - cameraPos.x,
                    rootWorldPos.z - cameraPos.z
                );

                // Calculate angle based on distance
                const angleOffset = distanceToBurger <= 0.6 ? 30 : 20; // 0.6 meters = 60 cm
                const newAngle = angleToCamera - THREE.MathUtils.degToRad(angleOffset);

                // Calculate new position at same distance but rotated angle
                const newX = cameraPos.x + Math.sin(newAngle) * distanceToBurger;
                const newZ = cameraPos.z + Math.cos(newAngle) * distanceToBurger;

                // Keep the same Y position as the burger
                openUIRef.position.set(newX, rootWorldPos.y, newZ);
                // Update mainUI position to match openUIRef
                updateMainUIPosition();
                // Show mainUI with scale animation
                mainUI.scale.set(0, 0, 0); // Reset scale before animation
                showMainUIWithAnimation();
            }
            
            console.log('Right hand released the root object - staying in current position and rotation');

            // After moving root, refit shadow frustum
            fitShadowCameraToObject(mainLight, root, 0.15);
        }
    }
    

    
    // Simple rotation mechanism (disabled when grabbing)
    if (!leftHandGrabbing && !rightHandGrabbing) {
        // Auto-rotation removed - object will remain stationary when not grabbed
        // root.rotation.y += 0.005; // Slow rotation around Y-axis
    }

}

function openingTracking() {

    if (joined) {

        if (rPinchOn && lPinchOn) {
            // Get world positions of the pinch references
            const lPinchWorldPos = new THREE.Vector3();
            const rPinchWorldPos = new THREE.Vector3();
            lPinchSphere.getWorldPosition(lPinchWorldPos);
            rPinchSphere.getWorldPosition(rPinchWorldPos);
            
            // Get world positions of the cubes
            const cubeAWorldPos = new THREE.Vector3();
            const cubeBWorldPos = new THREE.Vector3();
            cubeA.getWorldPosition(cubeAWorldPos);
            cubeB.getWorldPosition(cubeBWorldPos);
            
            // Calculate distances
            const lPinchToCubeA = lPinchWorldPos.distanceTo(cubeAWorldPos);
            const lPinchToCubeB = lPinchWorldPos.distanceTo(cubeBWorldPos);
            const rPinchToCubeA = rPinchWorldPos.distanceTo(cubeAWorldPos);
            const rPinchToCubeB = rPinchWorldPos.distanceTo(cubeBWorldPos);
            
            // Log the distances for debugging
            console.log('Distances:');
            console.log('Left pinch to Cube A:', lPinchToCubeA);
            console.log('Left pinch to Cube B:', lPinchToCubeB);
            console.log('Right pinch to Cube A:', rPinchToCubeA);
            console.log('Right pinch to Cube B:', rPinchToCubeB);
            
            // Require each hand to be near a different half to open
            const grabThreshold = 0.15; // Same threshold as used in handTracking
            const leftNearA = lPinchToCubeA < grabThreshold;
            const leftNearB = lPinchToCubeB < grabThreshold;
            const rightNearA = rPinchToCubeA < grabThreshold;
            const rightNearB = rPinchToCubeB < grabThreshold;

            // Only open when hands are near different halves, e.g., L near A and R near B (or vice versa)
            if ((leftNearA && rightNearB) || (leftNearB && rightNearA)) {
                joined = false;
                // Hide the UI plane when burger is opened
                mainUI.visible = false;
                
                // Hide arrow planes when burger is opened
                if (topArrowPlane) topArrowPlane.visible = false;
                if (bottomArrowPlane) bottomArrowPlane.visible = false;

                // Make all burger parts visible when opened
                [part0, part1, part2, part3, part4].forEach((part, index) => {
                    part.visible = switchStates[index];
                });

                // Remove positioning from here as we'll do it on release

                console.log('Switching to not joined mode - pinch detected near cube');
            }
        }

    }

}

function freeMoveTracking(){

    if (!joined) {
        // Get world positions of the pinch references
        const lPinchWorldPos = new THREE.Vector3();
        const rPinchWorldPos = new THREE.Vector3();
        lPinchSphere.getWorldPosition(lPinchWorldPos);
        rPinchSphere.getWorldPosition(rPinchWorldPos);
        
        // Get world positions of the cubes
        const cubeAWorldPos = new THREE.Vector3();
        const cubeBWorldPos = new THREE.Vector3();
        cubeA.getWorldPosition(cubeAWorldPos);
        cubeB.getWorldPosition(cubeBWorldPos);
        
        const grabThreshold = 0.15; // Same threshold as used elsewhere
        
        // Check left pinch
        if (lPinchOn) {
            const lPinchToCubeA = lPinchWorldPos.distanceTo(cubeAWorldPos);
            const lPinchToCubeB = lPinchWorldPos.distanceTo(cubeBWorldPos);
            
            // Move the closest cube if within threshold
            if (lPinchToCubeA < grabThreshold && lPinchToCubeA < lPinchToCubeB) {
                // Move cube A to left pinch position
                const localPos = new THREE.Vector3();
                localPos.copy(lPinchWorldPos);
                root.worldToLocal(localPos);
                cubeA.position.copy(localPos);
            } else if (lPinchToCubeB < grabThreshold && lPinchToCubeB < lPinchToCubeA) {
                // Move cube B to left pinch position
                const localPos = new THREE.Vector3();
                localPos.copy(lPinchWorldPos);
                root.worldToLocal(localPos);
                cubeB.position.copy(localPos);
                // When pinch is released and we're in open state, position the part0UIRef
                if (!joined) {
                    const cameraPos = new THREE.Vector3();
                    camera.getWorldPosition(cameraPos);

                    // Function to position a UI reference and plane for a part
                    const positionUIForPart = (part, uiRef, uiPlane) => {
                        const partWorldPos = new THREE.Vector3();
                        part.getWorldPosition(partWorldPos);

                        // Get vector from camera to part
                        const toPart = new THREE.Vector3().subVectors(partWorldPos, cameraPos);
                        
                        // Project vector onto XZ plane for angle calculation
                        const toPartXZ = new THREE.Vector3(toPart.x, 0, toPart.z);
                        const distance = toPart.length(); // Keep exact 3D distance
                        
                        // Calculate angle in XZ plane
                        const angleToCamera = Math.atan2(toPartXZ.x, toPartXZ.z);
                        
                        // Apply 20.5-degree offset while maintaining Y component
                        const newAngle = angleToCamera - THREE.MathUtils.degToRad(20.5);
                        
                        // Calculate new position maintaining exact distance
                        const xzDistance = toPartXZ.length();
                        const yOffset = toPart.y;
                        const newX = cameraPos.x + Math.sin(newAngle) * xzDistance;
                        const newZ = cameraPos.z + Math.cos(newAngle) * xzDistance;

                        // Set position maintaining the exact distance from camera
                        uiRef.position.set(newX, cameraPos.y + yOffset, newZ);
                        
                        // Update UI plane position to match reference
                        uiPlane.position.copy(uiRef.position);
                        uiPlane.visible = true;

                        // Make the plane face the camera
                        const deltaX = cameraPos.x - uiPlane.position.x;
                        const deltaZ = cameraPos.z - uiPlane.position.z;
                        const angleY = Math.atan2(deltaX, deltaZ);
                        uiPlane.rotation.set(0, angleY, 0);
                    };

                    // Position UI for all parts
                    positionUIForPart(part0, part0UIRef, part0UI);
                    positionUIForPart(part1, part1UIRef, part1UI);
                    positionUIForPart(part2, part2UIRef, part2UI);
                    positionUIForPart(part3, part3UIRef, part3UI);
                    positionUIForPart(part4, part4UIRef, part4UI);
                }
            }
        }
        
        // Check right pinch
        if (rPinchOn) {
            const rPinchToCubeA = rPinchWorldPos.distanceTo(cubeAWorldPos);
            const rPinchToCubeB = rPinchWorldPos.distanceTo(cubeBWorldPos);
            
            // Move the closest cube if within threshold
            if (rPinchToCubeA < grabThreshold && rPinchToCubeA < rPinchToCubeB) {
                // Move cube A to right pinch position
                const localPos = new THREE.Vector3();
                localPos.copy(rPinchWorldPos);
                root.worldToLocal(localPos);
                cubeA.position.copy(localPos);
            } else if (rPinchToCubeB < grabThreshold && rPinchToCubeB < rPinchToCubeA) {
                // Move cube B to right pinch position
                const localPos = new THREE.Vector3();
                localPos.copy(rPinchWorldPos);
                root.worldToLocal(localPos);
                cubeB.position.copy(localPos);
                // When pinch is released and we're in open state, position the part0UIRef
                if (!joined) {
                    const cameraPos = new THREE.Vector3();
                    camera.getWorldPosition(cameraPos);

                    // Function to position a UI reference and plane for a part
                    const positionUIForPart = (part, uiRef, uiPlane) => {
                        const partWorldPos = new THREE.Vector3();
                        part.getWorldPosition(partWorldPos);

                        // Get vector from camera to part
                        const toPart = new THREE.Vector3().subVectors(partWorldPos, cameraPos);
                        
                        // Project vector onto XZ plane for angle calculation
                        const toPartXZ = new THREE.Vector3(toPart.x, 0, toPart.z);
                        const distance = toPart.length(); // Keep exact 3D distance
                        
                        // Calculate angle in XZ plane
                        const angleToCamera = Math.atan2(toPartXZ.x, toPartXZ.z);
                        
                        // Apply 20.5-degree offset while maintaining Y component
                        const newAngle = angleToCamera - THREE.MathUtils.degToRad(20.5);
                        
                        // Calculate new position maintaining exact distance
                        const xzDistance = toPartXZ.length();
                        const yOffset = toPart.y;
                        const newX = cameraPos.x + Math.sin(newAngle) * xzDistance;
                        const newZ = cameraPos.z + Math.cos(newAngle) * xzDistance;

                        // Set position maintaining the exact distance from camera
                        uiRef.position.set(newX, cameraPos.y + yOffset, newZ);
                        
                        // Update UI plane position to match reference
                        uiPlane.position.copy(uiRef.position);
                        uiPlane.visible = true;

                        // Make the plane face the camera
                        const deltaX = cameraPos.x - uiPlane.position.x;
                        const deltaZ = cameraPos.z - uiPlane.position.z;
                        const angleY = Math.atan2(deltaX, deltaZ);
                        uiPlane.rotation.set(0, angleY, 0);
                    };

                    // Position UI for all parts
                    positionUIForPart(part0, part0UIRef, part0UI);
                    positionUIForPart(part1, part1UIRef, part1UI);
                    positionUIForPart(part2, part2UIRef, part2UI);
                    positionUIForPart(part3, part3UIRef, part3UI);
                    positionUIForPart(part4, part4UIRef, part4UI);
                }
            }
        }
    }

}

function rejoinTracking() {
    // Only check for rejoin when not joined and pinch detection is enabled
    if (!joined && pinchDetectionEnabled) {
        // Check if there are no pinches
        if (!lPinchOn && !rPinchOn) {
            // Get world positions of the cubes
            const cubeAWorldPos = new THREE.Vector3();
            const cubeBWorldPos = new THREE.Vector3();
            cubeA.getWorldPosition(cubeAWorldPos);
            cubeB.getWorldPosition(cubeBWorldPos);
            
            // Calculate distance between the two cubes
            const distanceBetweenCubes = cubeAWorldPos.distanceTo(cubeBWorldPos);
            
            // Define rejoin threshold (adjust this value as needed)
            const rejoinThreshold = 0.2; 
            
            // Check if distance is smaller than threshold
            if (distanceBetweenCubes < rejoinThreshold) {
                console.log('Rejoin detected! Distance between cubes:', distanceBetweenCubes, 'Threshold:', rejoinThreshold);
                
                // Calculate the midpoint position between the two halves
                const midpointPosition = new THREE.Vector3();
                midpointPosition.addVectors(cubeAWorldPos, cubeBWorldPos);
                midpointPosition.multiplyScalar(0.5);
                
                // Convert midpoint to local position within baseComposition
                const localMidpoint = new THREE.Vector3();
                localMidpoint.copy(midpointPosition);
                baseComposition.worldToLocal(localMidpoint);
                
                // Move root object to the calculated midpoint position
                root.position.copy(localMidpoint);
                
                // Move both halves to root position with gap between them
                
                // Update the burger name based on current ingredients
                const newBurgerName = getCurrentBurgerName();
                console.log('Rejoin Debug:', {
                    switchStates: [...switchStates],
                    resultingName: newBurgerName,
                    joined: joined
                });
                if (window.updateBurgerText) {
                    window.updateBurgerText(newBurgerName);
                }
                try {
                    // Calculate and apply new gap based on active ingredients
                    const currentGap = calculateGap();
                    const activeCount = switchStates.filter(state => state).length;
                    
                    console.log('Rejoin - Current state:', {
                        activeIngredients: activeCount,
                        calculatedGap: currentGap,
                        switchStates: [...switchStates]
                    });
                    
                    // Update positions with new gap and store as original positions
                    cubeA.position.set(0, currentGap, 0);
                    cubeB.position.set(0, -currentGap, 0);
                    
                    // Update original positions to match new gap
                    origPosA = cubeA.position.clone();
                    origPosB = cubeB.position.clone();
                    
                    // Reset both halves' rotations to 0,0,0 upon rejoin
                    cubeA.rotation.set(0, 0, 0);
                    cubeB.rotation.set(0, 0, 0);
                    
                    console.log('Gap adjustment complete:', {
                        gap: currentGap,
                        cubeAPos: cubeA.position.clone(),
                        cubeBPos: cubeB.position.clone(),
                        origPosA: origPosA.clone(),
                        origPosB: origPosB.clone()
                    });
                } catch (error) {
                    console.error('Error during gap adjustment:', error);
                }
                
                // Both cubes are already parented to root, so no reparenting needed
                // Just ensure they're in the correct relative positions
                
                // Activate joined mode so it can be grabbed again as original
                joined = true;
                // Hide all part UIs when burger is closed
                part0UI.visible = false;
                part1UI.visible = false;
                part2UI.visible = false;
                part3UI.visible = false;
                part4UI.visible = false;
                // Just make mainUI visible without animation for burger rejoin
                mainUI.visible = true;
                mainUI.scale.set(1, 1, 1);
                
                // Arrow planes stay invisible after first opening (no need to show them again)

                // After rejoin and repositioning, refit the shadow frustum
                fitShadowCameraToObject(mainLight, root, 0.15);

                // When burger is closed, position openUIRef 5 degrees to the right
                if (!leftHandGrabbing && !rightHandGrabbing) {
                    // Get the world position of the root (burger)
                    const rootWorldPos = new THREE.Vector3();
                    root.getWorldPosition(rootWorldPos);

                    // Get camera position
                    const cameraPos = new THREE.Vector3();
                    camera.getWorldPosition(cameraPos);

                    // Calculate distance from camera to burger
                    const distanceToBurger = rootWorldPos.distanceTo(cameraPos);

                    // Calculate angle between camera and burger in the XZ plane
                    const angleToCamera = Math.atan2(
                        rootWorldPos.x - cameraPos.x,
                        rootWorldPos.z - cameraPos.z
                    );

                    // Subtract 30 degrees from this angle (rotate to the left)
                    const newAngle = angleToCamera - THREE.MathUtils.degToRad(30);

                    // Calculate new position at same distance but rotated angle
                    const newX = cameraPos.x + Math.sin(newAngle) * distanceToBurger;
                    const newZ = cameraPos.z + Math.cos(newAngle) * distanceToBurger;

                    // Keep the same Y position as the burger
                    openUIRef.position.set(newX, rootWorldPos.y, newZ);
                    // Update mainUI position to match openUIRef
                    updateMainUIPosition();
                }
                
                console.log('Rejoin completed! Root moved to midpoint, cubes repositioned, joined = true');
            }
        }
    }
}

function openStateTracking() {
    if (!joined) {
    
        const _aPos = new THREE.Vector3();
        const _bPos = new THREE.Vector3();
        const _tmp = new THREE.Vector3();

        cubeA.getWorldPosition(_aPos);
        cubeB.getWorldPosition(_bPos);

        // Only orient when cubes are separated more than the threshold
        const orientationThreshold = 0.12; // meters
        const distanceAB = _aPos.distanceTo(_bPos);
        if (distanceAB <= orientationThreshold) {
            return;
        }

        // --- lookAt always points -Z to target ---
        cubeA.lookAt(_bPos);
        cubeB.lookAt(_aPos);

        // --- correction so the larger perpendicular face points to target ---
        // Use +Y so the larger face (0.9 x 0.9) faces the other cube
        const MODEL_FRONT = new THREE.Vector3(0, 1, 0);

        const corr = new THREE.Quaternion().setFromUnitVectors(
            new THREE.Vector3(0, 0, -1),
            MODEL_FRONT.clone().normalize()
        );

        cubeA.quaternion.multiply(corr);
        cubeB.quaternion.multiply(corr);

        // Add 180-degree rotation for cubeA when open and facing
        const flipRotation = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), Math.PI);
        cubeA.quaternion.multiply(flipRotation);
        
    }
}

function contentPartsTracking(){
    // Compute world-space positions between cubeA and cubeB and place parts there
    const aWorld = new THREE.Vector3();
    const bWorld = new THREE.Vector3();
    cubeA.getWorldPosition(aWorld);
    cubeB.getWorldPosition(bWorld);

    const dirAB = new THREE.Vector3().subVectors(bWorld, aWorld);

    // Helper to set a part's position at t along the AB segment and match rotation
    const aWorldQuat = new THREE.Quaternion();
    cubeA.getWorldQuaternion(aWorldQuat);

    const setPart = (mesh, t) => {
        const pos = new THREE.Vector3().copy(aWorld).addScaledVector(dirAB, t);
        mesh.position.copy(pos);
        mesh.quaternion.copy(aWorldQuat);
    };

    // Evenly distribute parts between A and B using t = (i+1)/(n+1)
    const parts = [part0, part1, part2, part3, part4];
    const n = parts.length;
    for (let i = 0; i < n; i++) {
        const t = (i + 1) / (n + 1);
        setPart(parts[i], t);
    }
}


// Helper function to update mainUI position to match openUIRef
function updateMainUIPosition() {
    mainUI.position.copy(openUIRef.position);
    
    // Get camera position
    const cameraPos = new THREE.Vector3();
    camera.getWorldPosition(cameraPos);
    
    // Calculate angle between plane and camera in the Y axis only
    const deltaX = cameraPos.x - mainUI.position.x;
    const deltaZ = cameraPos.z - mainUI.position.z;
    const angleY = Math.atan2(deltaX, deltaZ);
    
    // Apply only Y rotation, keep other axes at 0
    mainUI.rotation.set(0, angleY, 0);
}

function showMainUIWithAnimation() {
    // Make UI visible first
    mainUI.visible = true;
    // Animate scale from 0 to 1 with a nice elastic bounce
    gsap.to(mainUI.scale, {
        x: 1,
        y: 1,
        z: 1,
        duration: 0.25,
        ease: "power2.out",
        onStart: () => {
            console.log('Starting mainUI scale animation');
        },
        onComplete: () => {
            console.log('MainUI scale animation completed');
        }
    });
}

function rotateToCamera() {
    // Make the base composition face the camera along one horizontal axis (y-axis)
    let targetPosition = camera.position.clone();
    targetPosition.y = baseComposition.position.y; // Keep the y position same to rotate around y-axis only

    let targetQuaternion = new THREE.Quaternion();
    let currentQuaternion = baseComposition.quaternion.clone();

    // Calculate target quaternion for the base composition to look at the target position
    baseComposition.lookAt(targetPosition);
    targetQuaternion.copy(baseComposition.quaternion);
    baseComposition.quaternion.copy(currentQuaternion); // Revert to the original quaternion

    // Smoothly interpolate the rotation
    baseComposition.quaternion.slerp(targetQuaternion, 0.1);
}



